"use client";

/**
 * PORTED — RO-11-02 / #44587.
 * Origin: `excellent/app/_chrome/activity-logo.tsx`.
 *
 * Changes: next/link → @excellent/ui/router (RouterLink).
 * Copied into client/shell/chrome/ — never import from app/ (PRD D6).
 */

import * as React from "react";
import { RouterLink as Link } from "@excellent/ui/router";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  formatRelativeTime,
  useHydrated,
} from "@excellent/ui";
import { ArrowRight } from "@excellent/ui/icons";
import { useLiveResource } from "@excellent/ui/api/live";
import {
  liveActivity,
  type LiveActivityItem,
} from "@excellent/ui/api/contracts/live-activity";
import { LOGO_PATH, LOGO_VIEWBOX } from "./logo";

/**
 * The brandmark, wired to live background work. It IS the top-nav logo (replaces
 * the static `<Logo>`): clicking still goes Home, but now it also reflects what
 * the workspace is doing.
 *
 *   • WORKING → the eight stars TWINKLE like a shining constellation while work
 *     is in flight (a desynced per-star opacity+scale shimmer, floored so the
 *     mark never breaks up + a faint glow). This replaced the single breathing
 *     status dot that used to fill the open slot — the mark itself now says
 *     "something's running". The twinkle rests in the neutral foreground color.
 *   • EVENT → color, not choreography. On each start/complete/fail the whole
 *     mark FLUSHES to the status hue (start → primary, complete → success, fail →
 *     danger) and eases back to foreground — the twinkle keeps running underneath.
 *     One motion (the twinkle); color carries the meaning.
 *   • HOVER → a wave sweeps the mark L→R (each star swells, then settles), and a
 *     flyout lists the live work. Click → Home (`/`).
 *
 * Data is the existing `work` live-bus feed (`liveActivity.list`, the same feed
 * the bottom-right `LiveActivityDock` consumes). The twinkle is gated on
 * `useHydrated` (via `data-busy` on the link), so the resting SSR mark is
 * byte-identical to the static logo (no hydration flash even if a persisted SWR
 * cache holds prior work). The pulse baseline is primed from the first REAL
 * fetch (no seed), so work already in flight at load doesn't phantom-flash. The
 * hover wave + working twinkle are pure CSS (`:hover` / `[data-busy]`, per-star
 * timing via inline custom props); the event color flush rides `currentColor`
 * (the fill always, and the glow too while busy) animated on the star group.
 * Keyframes live in globals.css
 * (`logo-shimmer`/`logo-wave`/`logo-tint`) and all honour prefers-reduced-motion.
 * → docs/design/LOGO-ACTIVITY-PRD.md
 */

// ── geometry (one source of truth: LOGO_PATH, split per star) ───────────────
// Centers MUST stay aligned to the order LOGO_PATH concatenates its eight stars
// (viewBox 163): BR, BM, BL, TR, TM, TL, ML, MC — the middle-right slot stays
// empty (the mark's signature negative space). cx is used only to order the
// hover wave by column.
const STAR_META: Array<{ cx: number; cy: number }> = [
  { cx: 131.85, cy: 131.45 }, // BR
  { cx: 81.44, cy: 131.45 }, // BM
  { cx: 30.67, cy: 131.45 }, // BL
  { cx: 131.85, cy: 30.67 }, // TR
  { cx: 81.44, cy: 30.67 }, // TM
  { cx: 30.67, cy: 30.67 }, // TL
  { cx: 30.67, cy: 81.44 }, // ML
  { cx: 81.44, cy: 81.44 }, // MC
];
const STARS = LOGO_PATH.split("Z")
  .map((d) => d.trim())
  .filter((d) => d.length > 0)
  .map((d, i) => ({ d: d + "Z", ...STAR_META[i] }));

// Per-star motion params for the hover WAVE and the working TWINKLE. All derived
// deterministically from the star's index/position — no Date/random — so the
// server and the client's first paint agree (the CSS reads them as custom props):
//   • wi     — wave column (0 left … 2 right); hover sweeps L→R into the open slot.
//   • sdur   — twinkle cycle length, spread 2.4–3.7s so the eight never pulse together.
//   • sdelay — twinkle phase offset, 0–1.7s, for the same desync.
//   • lo     — this star's twinkle opacity floor: kept high enough that the whole
//              mark stays legible even at each star's dimmest point.
const STAR_MOTION = STARS.map((s, i) => ({
  wi: (s.cx ?? 0) < 50 ? 0 : (s.cx ?? 0) < 100 ? 1 : 2,
  sdur: 2.4 + ((i * 0.37) % 1.3),
  sdelay: (i * 0.53) % 1.7,
  lo: 0.52 + ((i * 7) % 3) * 0.07,
}));

type EventStatus = "start" | "done" | "fail";
// Each event flushes the whole mark to its status hue and eases back — carried by
// `currentColor` on the star group (the fill always; the glow too while work is
// still running), so the working twinkle keeps running underneath. Color is the
// only event signal.
const FLASH: Record<EventStatus, string> = {
  start: "var(--primary)",
  done: "var(--success)",
  fail: "var(--danger)",
};
const TINT_DUR = 1500; // ms — flush in, hold, then ease back to the resting foreground

/** Higher-salience event wins when several land in one tick. */
function moreSalient(a: EventStatus | null, b: EventStatus): EventStatus {
  const rank = { fail: 3, done: 2, start: 1 } as const;
  if (!a) return b;
  return rank[b] > rank[a] ? b : a;
}

function vars(obj: Record<string, string | number>): React.CSSProperties {
  return obj as React.CSSProperties;
}

export function ActivityLogo() {
  // No seed: `data` stays undefined until the first real fetch resolves, so the
  // diff baseline below is the first SERVER payload (not an empty seed) — work
  // already in flight at load primes the baseline silently instead of
  // phantom-flashing "start".
  const { data } = useLiveResource(
    liveActivity.list,
    {},
    { topic: "work", refreshInterval: 30_000 },
  );
  const hydrated = useHydrated();
  const items = data?.items ?? [];
  const busy = items.filter((i) => i.status === "running").length;

  // Detect start/complete/fail by diffing successive `work` payloads → one
  // ripple pulse per tick. The first payload only primes the baseline (no flash
  // for work that was already in flight when the page loaded).
  const prevRef = React.useRef<Map<string, LiveActivityItem["status"]>>(new Map());
  const seenRef = React.useRef(false);
  const seqRef = React.useRef(0);
  const [pulse, setPulse] = React.useState<{ seq: number; status: EventStatus } | null>(null);
  const gridRef = React.useRef<SVGGElement>(null);

  React.useEffect(() => {
    if (!data) return; // wait for the first real payload before baselining
    const cur = new Map(items.map((i) => [i.id, i.status]));
    if (!seenRef.current) {
      seenRef.current = true;
      prevRef.current = cur;
      return;
    }
    const prev = prevRef.current;
    let ev: EventStatus | null = null;
    for (const [id, st] of cur) {
      const was = prev.get(id);
      if (st === "running" && was !== "running") ev = moreSalient(ev, "start");
      else if (st === "done" && was === "running") ev = moreSalient(ev, "done");
      else if (st === "failed" && was === "running") ev = moreSalient(ev, "fail");
    }
    // A running item that vanished from the feed (finished + pruned, or a
    // terminal-less verify run that only ever reports "running") counts as a
    // completion — otherwise those finishes would never ripple.
    for (const [id, st] of prev) {
      if (st === "running" && !cur.has(id)) ev = moreSalient(ev, "done");
    }
    prevRef.current = cur;
    if (ev) {
      seqRef.current += 1;
      setPulse({ seq: seqRef.current, status: ev });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // On each event, flush the whole mark to the status hue then ease it back. The
  // tint animates the star group's `color`; the stars' fill + glow are
  // currentColor, so both track it, while the per-star twinkle underneath is
  // untouched. Restart via a forced reflow so two same-status events in a row
  // still re-flash (SVG has no offsetWidth, so read a layout box instead).
  React.useEffect(() => {
    const g = gridRef.current;
    if (!g || !pulse) return;
    g.style.setProperty("--la-flash", FLASH[pulse.status]);
    g.style.animation = "none";
    void g.getBoundingClientRect(); // reflow → restart the animation from 0
    g.style.animation = `logo-tint ${TINT_DUR}ms ease-in-out`;
  }, [pulse]);

  // ── flyout hover plumbing ──
  // Opening is gated on hover-INTENT: the logo is first a Home link, so a quick
  // pointer pass on the way to a click must NOT flash the flyout. We wait
  // ~1s of sustained hover before opening; a click (or leaving early) cancels
  // the pending open. Keeping it open while the pointer is on the flyout is
  // still instant (no re-delay) so the popover doesn't feel laggy in use.
  const HOVER_OPEN_DELAY = 1000; // ms of sustained hover before the flyout opens
  const [open, setOpen] = React.useState(false);
  const openTimer = React.useRef<number | undefined>(undefined);
  const closeTimer = React.useRef<number | undefined>(undefined);
  const clearTimers = () => {
    if (openTimer.current) window.clearTimeout(openTimer.current);
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
  };
  // Hover the logo: arm the delayed open (cancel any pending close first).
  const openLater = () => {
    clearTimers();
    openTimer.current = window.setTimeout(() => setOpen(true), HOVER_OPEN_DELAY);
  };
  // Pointer already on the open flyout: hold it open with no delay.
  const keepOpen = () => {
    clearTimers();
    setOpen(true);
  };
  // Only KEYBOARD focus should reveal the flyout. A pointer click, a programmatic
  // focus, or clicking back into the browser window/tab all fire `focus` on the
  // logo link too — and without this guard they'd arm the open timer and pop the
  // flyout ~1s later with no hover (the "popup shows when I click into the app"
  // bug: the browser restores focus to the logo when it was the last-focused
  // element). `:focus-visible` is exactly the browser's keyboard-vs-pointer
  // heuristic, so keyboard users still get the flyout; everyone else doesn't.
  const openOnKeyboardFocus = (e: React.FocusEvent<HTMLAnchorElement>) => {
    let keyboard = false;
    try {
      keyboard = e.currentTarget.matches(":focus-visible");
    } catch {
      keyboard = false; // engine without :focus-visible → never auto-open on focus
    }
    if (keyboard) openLater();
  };
  const closeSoon = () => {
    clearTimers(); // also cancels a pending open, so a quick pass never opens
    closeTimer.current = window.setTimeout(() => setOpen(false), 130);
  };
  const closeNow = () => {
    clearTimers();
    setOpen(false);
  };
  React.useEffect(() => () => clearTimers(), []);

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverAnchor asChild>
        <Link
          href="/"
          aria-label="Excellent home"
          data-busy={hydrated && busy > 0 ? "1" : undefined}
          onMouseEnter={openLater}
          onMouseLeave={closeSoon}
          onFocus={openOnKeyboardFocus}
          onBlur={closeSoon}
          onClick={closeNow}
          className="logo-act-link flex size-8 shrink-0 items-center justify-center rounded-md text-foreground outline-none transition-colors hover:bg-foreground/[0.06] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          <svg
            width={20}
            height={20}
            viewBox={`0 0 ${LOGO_VIEWBOX} ${LOGO_VIEWBOX}`}
            fill="none"
            className="overflow-visible"
            aria-hidden
          >
            {/* One star group. Its `color` is what an event flushes (logo-tint on
                gridRef); every star fills with currentColor + a currentColor glow,
                so the whole mark takes the status hue and eases back — the per-star
                twinkle/wave (below) rides underneath, undisturbed. */}
            <g ref={gridRef}>
              {STARS.map((star, i) => {
                const m = STAR_MOTION[i]!;
                return (
                  <path
                    key={i}
                    className="logo-act-star"
                    d={star.d}
                    fill="currentColor"
                    // Wave (--wi) + twinkle (--sdur/--sdelay/--lo) params ride as
                    // custom props; the CSS rules pick them up on hover / while busy.
                    style={vars({
                      "--wi": m.wi,
                      "--sdur": `${m.sdur}s`,
                      "--sdelay": `${m.sdelay}s`,
                      "--lo": m.lo,
                    })}
                  />
                );
              })}
            </g>
          </svg>
        </Link>
      </PopoverAnchor>

      <PopoverContent
        align="start"
        sideOffset={10}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onMouseEnter={keepOpen}
        onMouseLeave={closeSoon}
        className="w-80 p-0"
      >
        <ActivityFlyout items={items} busy={busy} onNavigate={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  );
}

const DOT: Record<LiveActivityItem["status"], string> = {
  running: "var(--primary)",
  done: "var(--success)",
  failed: "var(--danger)",
};

function ActivityFlyout({
  items,
  busy,
  onNavigate,
}: {
  items: LiveActivityItem[];
  busy: number;
  onNavigate: () => void;
}) {
  const hydrated = useHydrated();
  return (
    <div className="type-body">
      <div className="flex items-center justify-between border-b border-border/60 px-3 py-2.5">
        <span className="font-medium text-foreground">Activity</span>
        {busy > 0 ? (
          <span className="flex items-center gap-1.5 type-subtext text-muted-foreground">
            <span
              className="logo-act-bubble h-1.5 w-1.5 rounded-full"
              style={{ background: "var(--primary)", animation: "logo-halo 1.6s ease-in-out infinite" }}
            />
            {busy} running
          </span>
        ) : (
          <span className="type-subtext text-muted-foreground">idle</span>
        )}
      </div>

      {items.length > 0 ? (
        <ul className="max-h-72 overflow-auto py-1">
          {items.slice(0, 6).map((it) => (
            <li key={it.id}>
              <Link
                href={it.href}
                onClick={onNavigate}
                className="flex items-start gap-2.5 px-3 py-1.5 transition-colors hover:bg-secondary/50"
              >
                <span
                  className="logo-act-bubble mt-1.5 h-2 w-2 shrink-0 rounded-full"
                  style={{
                    background: DOT[it.status],
                    animation: it.status === "running" ? "logo-halo 1.6s ease-in-out infinite" : undefined,
                  }}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-foreground">{it.title}</span>
                  <span className="block truncate type-subtext text-muted-foreground">{it.detail}</span>
                </span>
                <span className="shrink-0 whitespace-nowrap type-subtext text-muted-foreground">
                  {it.status === "running"
                    ? "running"
                    : hydrated
                      ? formatRelativeTime(it.endedMs ?? it.startedMs)
                      : ""}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <div className="px-3 py-5 text-center type-subtext text-muted-foreground">
          Nothing running right now
        </div>
      )}

      <Link
        href="/activity"
        onClick={onNavigate}
        className="flex w-full items-center justify-between border-t border-border/60 px-3 py-2.5 type-body text-foreground transition-colors hover:bg-secondary/50"
      >
        View all activity
        <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
      </Link>
    </div>
  );
}
